/**
 * PaymentSchedulePanel — the main panel embedded inside InvoiceEditorPage.
 *
 * Displays existing milestones with status badges (Pending / Overdue / Paid),
 * a sum-validation indicator vs invoice total, and primary actions:
 *   - Bulk-create (opens BulkModal with 30/60/10 default)
 *   - Mark single milestone paid (opens MarkPaidModal)
 *   - Delete unpaid milestone
 *
 * Sun Stellar milestone-billing case: PaymentSchedulePanel must communicate
 * sum-mismatch loudly because field-staff bill against milestones during
 * site visits and a misaligned schedule causes recon pain at month-end.
 *
 * Print-friendly: includes @media print classes so the schedule renders
 * cleanly when the invoice is sent to PDF.
 */
import { useState, useMemo } from 'react';
import {
  CalendarClock,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Loader2,
  Banknote,
  Plus,
} from 'lucide-react';
import { formatINR, formatDate } from '@/utils/formatters';
import {
  usePaymentSchedules,
  type PaymentSchedule,
} from '@/hooks/usePaymentSchedules';
import { PaymentScheduleBulkModal } from './PaymentScheduleBulkModal';
import { PaymentScheduleMarkPaidModal } from './PaymentScheduleMarkPaidModal';

interface Props {
  invoiceId: string;
  invoiceTotal: number;
  /** When true, the invoice is brand-new / unsaved — panel shows hint instead of CTAs. */
  invoiceIsDraft?: boolean;
}

type RowStatus = 'paid' | 'overdue' | 'pending';

function statusOf(s: PaymentSchedule): RowStatus {
  if (s.paid) return 'paid';
  const due = new Date(s.dueDate);
  due.setHours(23, 59, 59, 999);
  if (due.getTime() < Date.now()) return 'overdue';
  return 'pending';
}

function StatusBadge({ status }: { status: RowStatus }) {
  if (status === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-200">
        <CheckCircle2 className="h-3 w-3" /> Paid
      </span>
    );
  }
  if (status === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200">
        <AlertTriangle className="h-3 w-3" /> Overdue
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
      Pending
    </span>
  );
}

export function PaymentSchedulePanel({
  invoiceId,
  invoiceTotal,
  invoiceIsDraft = false,
}: Props) {
  const {
    schedules,
    loading,
    error,
    scheduledTotal,
    createBulk,
    markPaid,
    removeOne,
  } = usePaymentSchedules(invoiceIsDraft ? null : invoiceId);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState<PaymentSchedule | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const delta = useMemo(
    () => Math.round((invoiceTotal - scheduledTotal) * 100) / 100,
    [invoiceTotal, scheduledTotal]
  );
  const hasSchedules = schedules.length > 0;
  const sumOk = !hasSchedules || Math.abs(delta) < 0.01;

  const paidCount = schedules.filter((s) => s.paid).length;
  const overdueCount = schedules.filter((s) => statusOf(s) === 'overdue').length;

  if (invoiceIsDraft) {
    return (
      <section className="mb-6 rounded-xl border border-gray-200 bg-white p-6 print:hidden">
        <header className="mb-2 flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-amber-600" />
          <h2 className="text-base font-semibold text-gray-900">Payment schedule</h2>
        </header>
        <p className="text-sm text-gray-500">
          Save this invoice as a draft first — then create milestones (advance / dispatch / commissioning, etc.).
        </p>
      </section>
    );
  }

  return (
    <section className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-amber-600" />
          <h2 className="text-base font-semibold text-gray-900">Payment schedule</h2>
          {hasSchedules && (
            <span className="text-xs text-gray-500">
              {schedules.length} milestone{schedules.length === 1 ? '' : 's'}
              {paidCount > 0 && ` · ${paidCount} paid`}
              {overdueCount > 0 && ` · ${overdueCount} overdue`}
            </span>
          )}
        </div>
        {hasSchedules && (
          <button
            type="button"
            data-testid="payment-schedule-bulk-modal-open-button"
            onClick={() => setShowBulkModal(true)}
            className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 print:hidden"
          >
            <Plus className="h-3.5 w-3.5" /> Bulk replace
          </button>
        )}
      </header>

      {/* Sum validation indicator — visible at all times when schedules exist */}
      {hasSchedules && (
        <div
          className={
            'mb-4 flex items-center justify-between rounded-lg border px-3 py-2 text-sm ' +
            (sumOk
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-amber-300 bg-amber-50 text-amber-900')
          }
        >
          <span className="flex items-center gap-2">
            {sumOk ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <span>
              Scheduled <span className="font-semibold">{formatINR(scheduledTotal)}</span>
              {' '}/ Invoice <span className="font-semibold">{formatINR(invoiceTotal)}</span>
            </span>
          </span>
          {!sumOk && (
            <span className="font-medium">
              Δ {formatINR(Math.abs(delta))} {delta > 0 ? 'short' : 'over'}
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-6 text-sm text-gray-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading schedule…
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !hasSchedules && (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center print:hidden">
          <p className="mb-3 text-sm text-gray-600">
            No payment schedule yet. Set up milestone billing for Sun Stellar–style
            advance / dispatch / commissioning splits.
          </p>
          <button
            type="button"
            data-testid="payment-schedule-create-first-button"
            onClick={() => setShowBulkModal(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
          >
            <Plus className="h-4 w-4" /> Bulk create 30 / 60 / 10
          </button>
        </div>
      )}

      {hasSchedules && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payment-schedules-table">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">#</th>
                <th className="px-3 py-2 text-left font-medium">Due date</th>
                <th className="px-3 py-2 text-left font-medium">Description</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Paid info</th>
                <th className="px-3 py-2 text-right font-medium print:hidden">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s, idx) => {
                const status = statusOf(s);
                const overdueRow = status === 'overdue';
                return (
                  <tr
                    key={s.id}
                    data-testid={`payment-schedule-row-${s.id}`}
                    className={
                      'border-t border-gray-100 ' +
                      (overdueRow ? 'bg-red-50/40' : '')
                    }
                  >
                    <td className="px-3 py-2 text-gray-500">
                      {s.sequence ?? idx + 1}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {formatDate(s.dueDate)}
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      {s.description || (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">
                      {formatINR(s.dueAmount)}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={status} />
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {s.paid ? (
                        <span>
                          {s.paidAmount != null && (
                            <>{formatINR(s.paidAmount)} · </>
                          )}
                          {s.modeOfPayment?.toUpperCase() ?? ''}
                          {s.paidAt && (
                            <>
                              {' '}on {formatDate(s.paidAt)}
                            </>
                          )}
                          {s.paymentRef && (
                            <span className="ml-1 font-mono text-[11px] text-gray-500">
                              {s.paymentRef}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right print:hidden">
                      <div className="flex justify-end gap-1">
                        {!s.paid && (
                          <button
                            type="button"
                            data-testid={`payment-schedule-mark-paid-button-${s.id}`}
                            onClick={() => setMarkPaidTarget(s)}
                            className="inline-flex items-center gap-1 rounded border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
                          >
                            <Banknote className="h-3.5 w-3.5" /> Mark paid
                          </button>
                        )}
                        {!s.paid && (
                          <button
                            type="button"
                            data-testid={`payment-schedule-delete-button-${s.id}`}
                            disabled={deletingId === s.id}
                            onClick={async () => {
                              if (!confirm('Delete this milestone?')) return;
                              setDeletingId(s.id);
                              await removeOne(s.id);
                              setDeletingId(null);
                            }}
                            className="inline-flex items-center rounded border border-gray-200 bg-white p-1 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            aria-label="Delete milestone"
                          >
                            {deletingId === s.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PaymentScheduleBulkModal
        open={showBulkModal}
        invoiceTotal={invoiceTotal}
        onClose={() => setShowBulkModal(false)}
        onSubmit={async (inputs) => {
          await createBulk(inputs);
        }}
      />
      <PaymentScheduleMarkPaidModal
        open={markPaidTarget !== null}
        schedule={markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onSubmit={async (input) => {
          if (markPaidTarget) {
            await markPaid(markPaidTarget.id, input);
          }
        }}
      />
    </section>
  );
}

export default PaymentSchedulePanel;
